import * as React from 'react';
import {useRef} from 'react';
import {
  createView,
  createViewConfig,
  view,
  assignProps,
  provider,
  dynamic,
  useIsomorphicLayoutEffect,
} from '@sail/engine';
import type {View} from '@sail/engine';

import type {
  CollectionChildren,
  Key,
  SingleSelection,
} from '@react-types/shared';
import type {AriaTabPanelProps, TabListState} from '@sail/react-aria';
import {
  useTabPanel,
  useTab,
  useTabList,
  useFocusVisible,
} from '@sail/react-aria';
import {chevronDown} from '@sail/icons/react/Icon';
import type {Href} from '@sail/global-state';
import {tokens} from '@sail/tokens';
import {css} from '../../css';
import {Menu, MenuItem} from '../Menu';
import {Icon} from '../Icon';
import type {ActionProps} from '../Action';
import {Action} from '../Action';
import type {Collection, Item} from '../../internal/collections';
import {
  CollectionProvider,
  useCollection,
  useItem,
  useTabListState,
} from '../../internal/collections';
import {useCollapsibleTabs} from './useCollapsibleTabs';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {TabListStateContext} from './TabListStateContext';
import {TabLine} from './TabLine';
import {TRUNCATION_WRAPPER_STYLES} from './internal';

const {createContext, useContext, useState, useMemo, useEffect, useCallback} =
  React;

/**
 * @external
 */
interface TabProps extends ActionProps {
  /**
   * @external
   * Whether or not the tab should be disabled
   */
  disabled: boolean;
  /**
   * @external
   * Children passed to Tab component
   */
  children: React.ReactNode;

  ref?: React.Ref<HTMLAnchorElement>;

  href?: Href;
}

/**
 * TODO: This should expose the intrinsic props and forward them to
 * the div that this renders
 *
 * See https://git.corp.stripe.com/stripe-internal/pay-server/pull/587667
 *
 * @external
 */
interface TabsProps {
  /**
   * @external
   * Children passed to Tabs component
   */
  children: React.ReactNode;

  /**
   * @external
   * Size of Tabs component, can be 'small', 'medium' or 'large'
   */
  size: 'small' | 'medium' | 'large' | 'medium-compact';

  /**
   * @external
   * Whether or not the tab list should take up the entire width of its container
   */
  fitted: boolean;

  /**
   * @external
   * Key of currently selected Tab panel (controlled)
   */
  selectedKey?: Key;

  /**
   * @external
   * The initially selected Tab panel (uncontrolled).
   */
  defaultSelectedKey?: Key;

  /**
   * @external
   * Callback to be fired when a Tab is selected
   */
  onSelectionChange?: (arg: Key) => void;

  /**
   * Whether keyboard navigation requires selection to change the tab
   */
  keyboardSelection?: 'press' | 'focus';

  /**
   * @external
   * className to be applied to the containing div
   */
  className?: string;
}

export const TabsConfig = createViewConfig({
  name: 'Tabs',
  props: {} as TabsProps,
  flattens: true,
  defaults: {
    size: 'medium',
    fitted: false,
  },
});

export const TabConfig = createViewConfig({
  name: 'Tab',
  props: {} as TabProps,
  flattens: true,
  defaults: {
    disabled: false,
  },
});

export const TabListConfig = createViewConfig({
  name: 'TabList',
  props: {} as {
    children: CollectionChildren<TabProps>;
    overflowMenuLabel?: string;
    overflowMenuAriaLabel?: string;
  },
});

export const TabPanelConfig = createViewConfig({
  name: 'TabPanel',
  props: {} as AriaTabPanelProps & View.IntrinsicElement<'div'>,
});

export const TabPanelsConfig = createViewConfig({
  name: 'TabPanels',
  props: {} as {children: React.ReactNode},
});

type ListState = TabListState<unknown> & {collapsed?: boolean};
type TabOptions = Pick<TabsProps, 'size' | 'fitted' | 'keyboardSelection'> &
  SingleSelection;

const TabOptionsContext = createContext<TabOptions | null>(null);
const TabListStateUpdaterContext = createContext<
  ((arg: ListState) => void) | null
>(null);
const TabStretchedContext = createContext(false);
const TabHasOverflowContext = createContext(false);
const TruncatedTabIdContext = createContext<string | null>(null);

/**
 * @external
 */
export const Tabs = TabsConfig.createView(
  ({children, className, inherits, ...props}) => {
    const [state, setState] = useState<ListState | null>(null);
    const {isFocusVisible} = useFocusVisible();

    return (
      <view.div
        inherits={inherits}
        className={className}
        uses={[
          provider(TabOptionsContext, props),
          provider(TabListStateUpdaterContext, setState),
          provider(TabListStateContext, state),
          TabConfig.customize({
            css: isFocusVisible
              ? css({':focus::after': {focusRing: 'focus'}})
              : css({':focus::after': {focusRing: 'none'}}),
          }),
        ]}
      >
        {children}
      </view.div>
    );
  },
  {
    variants: {
      fitted: {
        true: {
          css: {
            width: 'fill',
          },
          uses: [
            TabConfig.customize({
              css: {
                '::after': {
                  left: 'space.0',
                  right: 'space.0',
                },
              },
            }),
          ],
        },
      },
    },
    forward: {
      fitted: true,
    },
  },
);

export const StyledTabConfig = createViewConfig({
  props: {} as {
    size?: TabsProps['size'];
    selected?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    hidden?: boolean;
    first?: boolean;
    last?: boolean;
  },
  flattens: true,
});

/**
 * Applies the styles of a tab, without exposing size/selected variants on the public Tabs API.
 * Component is used for the collapsed menu trigger and within the exported Tab component.
 */
const StyledTab = StyledTabConfig.createView((props) => <Action {...props} />, {
  css: {
    zIndex: 0,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    font: 'label.medium.emphasized',
    color: 'subdued',
    position: 'relative',
    stack: 'x',
    gapX: 'small',
    alignX: 'center',
    paddingX: 'space.150',
    transitionDuration: 'short',
    transitionProperty: 'color',
    transitionTimingFunction: 'enter',
    ':focus': {focusRing: 'none'},
    ':hover': {color: 'text'},
    ':hover::after': {backgroundColor: 'offset'},
    '::after': {
      content: `''`,
      zIndex: -1,
      top: 'xsmall',
      left: 'xsmall',
      right: 'xsmall',
      bottom: 'xsmall',
      position: 'absolute',
      borderRadius: 'medium',
      transitionDuration: 'short',
      transitionTimingFunction: 'enter',
      transitionProperty: 'background-color',
    },
  },
  uses: [
    assignProps(() => {
      // apply size applied from outer `Tabs` component
      const {size} = useContext(TabOptionsContext) || {};
      return {size: size || 'medium'};
    }),
    dynamic(() => {
      const uiConfig = useUiConfig();

      if (uiConfig?.disableReactAriaFocusRing) {
        return [
          css({
            ':focus-visible': {
              focusRing: 'none',
            },
          }),
        ];
      }

      return [];
    }),
  ],
  forward: {disabled: true},
  variants: {
    size: {
      small: css({
        paddingY: 'xsmall',
        font: 'label.small',
        fontWeight: 'semibold',
      }),
      // TODO: temporary variant for the experimental rollout of Sail X Tabs in dashboard payments.
      'medium-compact': css({
        paddingY: 'small',
      }),
      medium: css({paddingY: 'medium'}),
      large: css({paddingY: 'space.250'}),
    },
    disabled: {
      true: css({
        cursor: 'default',
        color: 'form.disabled',
        ':hover': {color: 'form.disabled'},
        ':hover::after': {backgroundColor: 'none'},
      }),
    },
    selected: {
      true: css({
        color: 'action.primary',
        ':hover': {color: 'action.primary.hovered'},
        ':hover::after': {backgroundColor: tokens['color.primary.25']},
      }),
    },
    hidden: {
      true: css({visibility: 'hidden'}),
    },
    first: {
      true: css({marginLeft: '-12px'}),
    },
    last: {
      true: css({marginRight: '-12px'}),
    },
  },
});

/**
 * @external
 */
export const Tab = TabConfig.createView((props) => {
  const {id, children, href, rel, target, disabled} = props;
  const ref = useRef<HTMLAnchorElement>(null);
  const data = useMemo(
    () => ({
      children,
      target,
      rel,
      href,
      disabled,
    }),
    [children, target, href, rel, disabled],
  );
  const {key} = useItem({typeaheadValue: '', id, data});
  const state = useContext(TabListStateContext) as ListState;
  const {tabProps, isSelected} = useTab(
    {key, isDisabled: disabled},
    state,
    ref,
  );

  // TODO(craigcav): This pattern is likely not compatible with concurrent-mode.
  ///   Can we modify this data in an effect?
  //
  // sync the disabled prop back into state
  if (disabled) {
    state?.disabledKeys.add(key);
  } else {
    state?.disabledKeys.delete(key);
  }
  const isStretched = useContext(TabStretchedContext);
  const hasOverflow = useContext(TabHasOverflowContext);
  const isTruncated = useContext(TruncatedTabIdContext) === String(key);
  const isFirst = !isStretched && state?.collection?.getFirstKey?.() === key;
  const isLast =
    !isStretched && !hasOverflow && state?.collection?.getLastKey?.() === key;
  const variants = {
    selected: isSelected,
    hidden: state.collapsed,
    first: isFirst,
    last: isLast,
  };
  return (
    <StyledTab
      {...props}
      data-collection-id={key}
      data-stretched={isStretched || undefined}
      {...tabProps}
      {...variants}
      ref={ref}
    >
      {isTruncated ? (
        <view.span data-truncation-wrapper style={TRUNCATION_WRAPPER_STYLES}>
          {children}
        </view.span>
      ) : (
        children
      )}
    </StyledTab>
  );
});

type TabOverflowMenuProps = {
  items: Array<Item<TabProps>>;
  selectedId: Key;
  onSelect: (id: Key) => void;
  label?: string;
  ariaLabel?: string;
  triggerRef: React.Ref<HTMLElement>;
};

const TabOverflowMenu = createView(
  ({
    items,
    selectedId,
    onSelect,
    triggerRef,
    inherits,
    label = 'More',
    ariaLabel,
  }: View.RenderProps<TabOverflowMenuProps>) => {
    return (
      <Menu
        inherits={inherits}
        trigger={
          <StyledTab
            ref={triggerRef}
            aria-hidden={items.length === 0}
            aria-label={ariaLabel}
            last
            css={{stack: 'x', gap: 'small'}}
          >
            {label}
            <Icon icon={chevronDown} size="xsmall" />
          </StyledTab>
        }
        onAction={(key: Key) => {
          onSelect(key);
        }}
        subviews={{
          popover: assignProps({placement: 'bottom'}),
        }}
      >
        {items.map((item) => {
          const {disabled} = item.data;
          const selected = item.key === selectedId;

          return (
            <MenuItem
              id={item.key}
              key={item.key}
              href={item.data.href}
              target={item.data.target}
              rel={item.data.rel}
              disabled={disabled}
              css={[
                !disabled
                  ? css({
                      color: 'subdued',
                    })
                  : css({}),
                !disabled && selected
                  ? css({
                      color: 'action.primary',
                      ':hover': {
                        color: 'action.primary.hovered',
                        backgroundColor: tokens['color.primary.25'],
                      },
                    })
                  : css({}),
              ]}
            >
              {item.data.children}
            </MenuItem>
          );
        })}
      </Menu>
    );
  },
);

/**
 * @external
 */
export const TabList = TabListConfig.createView(
  ({children, overflowMenuLabel, overflowMenuAriaLabel, ...props}) => {
    const ref = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const measurementNodeRef = useRef(null);
    const collection = useCollection<TabProps>(children as React.ReactNode);

    // TODO: respect `fitted` here
    const {
      selectedKey,
      defaultSelectedKey,
      onSelectionChange,
      keyboardSelection,
      fitted,
    } = useContext(TabOptionsContext) || {};

    const collapsible = useCollapsibleTabs(
      measurementNodeRef,
      ref,
      menuRef,
      selectedKey || defaultSelectedKey,
    );

    const getItems = collection.getItems;
    const items = useMemo(
      () => getItems().filter((item) => !collapsible.hiddenIds.has(item.key)),
      [getItems, collapsible.hiddenIds],
    );
    const itemsForMenu = useMemo(
      () => getItems().filter((item) => collapsible.menuItems.has(item.key)),
      [getItems, collapsible.menuItems],
    );

    const state = useTabListState({
      getItems: useCallback(() => items, [items]),
      overflowHiddenKeys: collapsible.hiddenIds,
      selection: {
        selectedKey,
        defaultSelectedKey,
        onSelectionChange: (key: Key) => {
          onSelectionChange?.(key);
        },
      },
    });

    const collapsibleSetSelected = collapsible.setSelected;
    const collapsibleUpdateOverflow = collapsible.updateOverflow;

    const prevSelectedKeyRef = useRef(state.selectedKey);

    // Run overflow layout before the first paint so the "More" trigger does not
    // flash while we wait for ResizeObserver. Visibility is managed via inline
    // styles in useCollapsibleTabs, not CSS classes on the trigger.
    useIsomorphicLayoutEffect(() => {
      collapsibleUpdateOverflow();
    }, [collapsibleUpdateOverflow]);

    // Sync the selected key and re-run overflow layout before the browser
    // paints to avoid a visible flicker when the selected tab changes.
    // useIsomorphicLayoutEffect matches the timing of useResizeObserver.
    //
    // We cannot rely on `onSelectionChange` to do this sync due to a
    // workaround in react-aria [0] where `onSelectionChange` is called with
    // the previous selected key when the new selected item is a link.
    // [0] https://github.com/adobe/react-spectrum/blob/%40react-aria/selection%403.17.1/packages/%40react-aria/selection/src/useSelectableItem.ts#L136
    useIsomorphicLayoutEffect(() => {
      collapsibleSetSelected(state.selectedKey);
      if (prevSelectedKeyRef.current !== state.selectedKey) {
        prevSelectedKeyRef.current = state.selectedKey;
        collapsibleUpdateOverflow();
      }
    }, [collapsibleSetSelected, collapsibleUpdateOverflow, state.selectedKey]);

    const {tabListProps} = useTabList(
      {
        keyboardActivation:
          keyboardSelection === 'press' ? 'manual' : 'automatic',
      },
      state,
      ref,
    );

    const setTabState = useContext(TabListStateUpdaterContext);

    useEffect(() => {
      // Passing back to root as useTabPanel needs the TabListState
      setTabState?.(state);
      // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
    }, [state.disabledKeys, state.selectedItem, state.selectedKey]);

    // The DOM structure below is important for a few reasons:
    // * Users can apply arbitrary CSS to the outer div
    //
    // * We create another inner div that we actually track as
    //   the node to measure the available space. We could combine
    //   this div with the outer one, but the downside is the user
    //   could apply padding to Tabs and either we accept that that
    //   breaks our measurement code, or our measurement code has to
    //   call getComputedStyle which I'd really like to avoid because
    //   it's expensive
    //
    // * A third div wraps the actual tab list, and is where the
    //   react-aria tab list props are applied. This allows keyboard
    //   navigation to work: the menu has to be outside of the
    //   react-aria tab list so the user can tab to it. Right now the
    //   experience is: press left/right to switch tabs and it doesn't
    //   focus the menu, but pressing tab moves to the menu (as if
    //   they are separate pieces). I think that's fine. The menu has
    //   to be outside of the tab list for this to work; if it's
    //   inside it's never focusable
    return (
      <view.div {...props}>
        <view.div
          ref={measurementNodeRef}
          css={{gap: 'space.0'}}
          uses={[css({display: 'flex', gap: 'space.0'})]}
        >
          <view.div
            {...tabListProps}
            ref={ref}
            css={[
              css({
                minWidth: 'space.0',
                stack: 'x',
                gap: 'space.0',
              }),
              fitted && itemsForMenu.length === 0
                ? css({alignX: 'stretch', flex: 1})
                : css({alignX: 'start'}),
            ]}
          >
            <TabStretchedContext.Provider
              value={!!fitted && itemsForMenu.length === 0}
            >
              <TabHasOverflowContext.Provider value={itemsForMenu.length > 0}>
                <TabListStateContext.Provider value={state}>
                  <TruncatedTabIdContext.Provider
                    value={collapsible.truncatedId}
                  >
                    <CollectionProvider
                      collection={collection as Collection<unknown>}
                    >
                      {collection.children}
                    </CollectionProvider>
                  </TruncatedTabIdContext.Provider>
                </TabListStateContext.Provider>
              </TabHasOverflowContext.Provider>
            </TabStretchedContext.Provider>
            <TabLine />
          </view.div>
          <TabOverflowMenu
            label={overflowMenuLabel}
            ariaLabel={overflowMenuAriaLabel}
            triggerRef={menuRef}
            items={itemsForMenu}
            selectedId={state.selectedKey}
            onSelect={(key) => {
              state.setSelectedKey(key);

              // We could do all of this work in `onSelectionChange` but that
              // means switching any tabs reruns the overflow logic. As a
              // small perf optimization, we know we only need to rerun
              // it when selecting a tab from the menu, so we eagerly do it
              // here
              collapsible.setSelected(key as string);
              collapsible.updateOverflow();
            }}
          />
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      position: 'relative',
      borderColor: 'border',
      borderBottomWidth: 'small',
      borderBottomStyle: 'solid',
    },
  },
);

/**
 * @external
 */
export const TabPanel = TabPanelConfig.createView(
  ({children, ...props}) => {
    const state = useContext(TabListStateContext) as ListState;
    const ref = React.useRef<HTMLDivElement>(null);
    const {tabPanelProps} = useTabPanel(props, state, ref);

    const selected = props.id && state?.selectedKey === props.id;

    return selected ? (
      <view.div ref={ref} {...props} {...tabPanelProps}>
        {children}
      </view.div>
    ) : null;
  },
  {
    css: {
      ':focus-visible': {
        focusRing: 'focus',
      },
    },
  },
);

/**
 * @external
 */
export const TabPanels = TabPanelsConfig.createView(({children}) => {
  const state = useContext(TabListStateContext);

  return (
    <React.Fragment key={state?.selectedKey}>
      {state && state.selectedKey ? children : null}
    </React.Fragment>
  );
});
